<template>
    <div>

        <!-- Step 1 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 1">
                <h4 class="mb-3">Calcolo Retta</h4>
                <form @submit.prevent="submitStep1">
                    <div class="p-4 p-md-5 bg-white border rounded">

                        <!-- Nome -->
                        <div class="row mb-3">
                            <label for="firstName" class="col-md-4 col-form-label text-md-end">Nome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="firstName" v-model="form.first_name" :readonly="first_name" required>
                            </div>
                        </div>

                        <!-- Cognome -->
                        <div class="row mb-3">
                            <label for="lastName" class="col-md-4 col-form-label text-md-end">Cognome</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" id="lastName" v-model="form.last_name" :readonly="last_name" required>
                            </div>
                        </div>

                        <!-- Codice Fiscale -->
                        <div class="row mb-3">
                            <label for="fiscalCode" class="col-md-4 col-form-label text-md-end">Codice Fiscale</label>
                            <div class="col-12 col-md-8">
                                <input type="text" class="form-control" :class="{ 'is-invalid': !validation.fiscal_code }" id="fiscalCode" v-model="form.fiscal_code" v-uppercase :readonly="fiscal_code || validation.promo_code === 'success'" required>
                                <div class="invalid-feedback" v-if="!validation.fiscal_code">
                                    Codice Fiscale non valido o non corrispondente al nome e/o al cognome.
                                </div>
                            </div>
                        </div>

                        <hr />

                        <!-- Corso -->
                        <div class="row mb-3" v-if="!course">
                            <label for="course" class="col-md-4 col-form-label text-md-end">Corso</label>
                            <div class="col-12 col-md-8">
                                <v-select 
                                    :options="helpers.courses" 
                                    v-model="form.course_select" 
                                    :clearable="false"
                                    @update:modelValue="changeCourse">   
                                </v-select>
                            </div>
                        </div>

                        <!-- ECP -->
                        <div class="row mb-3">
                            <label for="ecp" class="col-md-4 col-form-label text-md-end">{{ university.ecp_text }}</label>
                            <div class="col-12 col-md-8">
                                <div v-if="form.ecp">
                                    <v-select 
                                        :options="helpers.ecps" 
                                        v-model="form.ecp" 
                                        :clearable="false" 
                                        :disabled="!!ecp || ecp_code"
                                        @update:modelValue="getConventions">
                                    </v-select>
                                </div>
                                <div v-else>
                                    <app-placeholder />
                                </div>
                            </div>
                        </div>

                        <!-- Convenzione -->
                        <div class="row mb-3">
                            <label for="convention" class="col-md-4 col-form-label text-md-end">Convenzione</label>
                            <div class="col-12 col-md-8">
                                <div v-if="form.convention">
                                    <v-select 
                                        :options="helpers.conventions" 
                                        v-model="form.convention" 
                                        :clearable="false" 
                                        :disabled="!!convention"
                                    >
                                    </v-select>
                                </div>
                                <div v-else>
                                    <app-placeholder />
                                </div>
                            </div>
                        </div>

                        <!-- Programma -->
                        <div class="row mb-3" v-if="validation.promo_code_type != 'program'">
                            <label for="program" class="col-md-4 col-form-label text-md-end">Programma</label>
                            <div class="col-12 col-md-8">
                                <select 
                                    class="form-select" 
                                    aria-label="Programma" 
                                    id="program" 
                                    v-model="form.program" 
                                    @change="getImport" 
                                    :disabled="!!program"
                                    required
                                >
                                    <option :value="null" disabled>Seleziona</option>
                                    <option v-for="program in helpers.programs" :key="program.id" :value="{ id: program.id, label: program.label }">
                                        {{ program.label }}
                                    </option>
                                </select>
                            </div>
                        </div>

                        <!-- Promo Code -->
                        <div class="row mb-3">
                            <label for="label" class="col-md-4 col-form-label text-md-end">Codice Promo</label>
                            <div class="col-12 col-md-8">
                                <div class="input-group">
                                    <input 
                                        type="text" 
                                        v-model="form.promo_code" 
                                        class="form-control"
                                        :readonly="validation.promo_code == 'success'" 
                                        :class="{ 'is-invalid': validation.promo_code === 'fail', 'is-valid': validation.promo_code === 'success' }">
                                    <button class="btn btn-outline-secondary" type="button" @click="getPromoCode" :disabled="validation.promo_code === 'success'">Applica</button>
                                    <div class="invalid-feedback" v-if="validation.promo_code === 'fail'">
                                        {{ validation.promo_code_message }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Codice MGM -->
                        <div class="row mb-3 d-none">
                            <label for="label" class="col-md-4 col-form-label text-md-end">
                                Codice MGM
                                <div class="custom-tooltip">
                                    <i class="fas fa-question-circle text-muted"></i>
                                    <div class="custom-tooltip-top border bg-white rounded shadow p-3">
                                        <p class="m-0">Il Codice MGM è un codice fornito mediante link di invito da uno studente iscritto all'Ateneo</p>
                                    </div>
                                </div>
                            </label>
                            <div class="col-12 col-md-8">
                                <div class="input-group">
                                    <input 
                                        type="text" 
                                        v-model="form.mgm_code" 
                                        class="form-control"
                                    >
                                </div>
                            </div>
                        </div>

                        <!-- Privacy -->
                        <div class="row mb-2">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.privacy" id="privacy" value="1" required>
                                    <label class="form-check-label text-secondary" for="privacy">
                                        <strong>Obbligatorio</strong> Dichiaro di aver letto e compreso l'informativa sul trattamento dei dati personali
                                        dell'<strong>{{ university.name }}</strong> (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Marketing -->
                        <div class="row mb-2">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.marketing" id="marketing" value="1">
                                    <label class="form-check-label text-secondary" for="marketing">
                                        Acconsento al trattamento dei dati personali per la finalità di marketing dell'<strong>{{ university.name }}</strong>
                                        - con strumenti tradizionali (chiamate tramite operatore) e automatizzati (e-mail, instant messaging e popup) e permettendo anche l'ottimizzazione dei risultati delle campagne mediante pixel - 
                                        meglio descritta al paragrafo 4 (finalità n. 10) dell'informativa sul trattamento dei dati personali 
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Statistiche -->
                        <div class="row">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.statistics" id="statistics" value="1">
                                    <label class="form-check-label text-secondary" for="statistics">
                                        Acconsento al trattamento dei dati personali per la finalità di analisi statistiche dell'<strong>{{ university.name }}</strong> 
                                        meglio descritta al paragrafo 4 (finalità n. 11) dell’informativa sul trattamento dei dati personali  
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Alumni -->
                        <div class="row">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.alumni" id="alumni" value="1">
                                    <label class="form-check-label text-secondary" for="alumni">
                                        Acconsento al trattamento dei dati per gestire la mia adesione all'Associazione Alumni dell'<strong>{{ university.name }}</strong>, 
                                        meglio descritta al paragrafo 4 (finalità n. 15) dell’informativa sul trattamento dei dati personali
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>
                        <!-- Progetti -->
                        <div class="row">
                            <div class="col-12 col-md-8 offset-md-4">
                                <div class="form-check small lh-sm">
                                    <input class="form-check-input" type="checkbox" v-model="form.progetti" id="progetti" value="1">
                                    <label class="form-check-label text-secondary" for="progetti">
                                        Acconsento al trattamento dei dati per ricevere inviti a partecipare a questionari nell’ambito di progetti di ricerca
                                        dell'<strong>{{ university.name }}</strong>, come meglio descritto al paragrafo 4 (finalità n. 17) dell’informativa sul trattamento dei dati
                                        personali
                                        (<a :href="university.privacy_student" title="Informativa" target="_blank" class="link-secondary">Informativa</a>)
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Validazione + Email -->
                        <div class="alert alert-danger mt-3" role="alert" v-if="!validation.enrol">
                            <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                            <p class="mb-0">{{ validation.message }}</p>
                            <button type="button" class="btn btn-outline-danger mt-3" v-if="validation.sendEmail && !temp.sendedEmail" @click="sendEmailTemp">
                                <i class="fas fa-circle-notch fa-spin" v-if="temp.sendingEmail"></i>
                                <i class="far fa-envelope me-2" v-else></i>
                                Reinvia e-mail per l'accesso all'area personale
                            </button>
                        </div>
                        <div class="alert alert-success mt-3" v-if="temp.sendedEmail">
                            <h5><i class="fas fa-check-circle me-2"></i>E-Mail inviata</h5>
                            <p class="mb-0">Controlla la tua casella di posta elettronica.</p>
                        </div>
                    </div>
                    <div class="pt-3 text-end">
                        <button class="btn btn-success" type="submit" :disabled="helpers.sending">
                            Prosegui
                            <i class="fas fa-circle-notch fa-spin ms-2" v-if="helpers.sending"></i>
                            <i class="fas fa-angle-right ms-2" v-else></i>
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 2 -->
        <transition name="fade" mode="out-in">
            <div v-if="step == 2">
                <h4 class="mb-3">Dati Anagrafici</h4>
                <form @submit.prevent="submitStep2">
                    <div class="p-4 p-md-5 bg-white border rounded">
                        <!-- Sesso -->
                        <div class="row mb-3">
                            <label for="gender" class="col-md-4 col-form-label text-md-end">Sesso</label>
                            <div class="col-12 col-md-8">
                                <select class="form-select" aria-label="Sesso" id="gender" v-model="form.gender" required="required">
                                    <option value="" disabled>Seleziona</option>
                                    <option value="M">Maschio</option>
                                    <option value="F">Femmina</option>
                                </select>
                            </div>
                        </div>
                        <!-- Data di Nascita -->
                        <div class="row mb-3">
                            <label for="birthDate" class="col-md-4 col-form-label text-md-end">Data di Nascita</label>
                            <div class="col-12 col-md-8">
                                <VueDatePicker 
                                    v-model="form.birth_date"
                                    model-type="format"
                                    format="yyyy-MM-dd"
                                    valueType="format"
                                    class="w-100"
                                    locale="it"
                                    placeholder="aaaa-mm-gg"
                                    default-panel="year"
                                    :enable-time-picker="false"
                                    :input-attr="{ required: true }">
                                </VueDatePicker>
                                <span v-if="validation.birth_date" class="small m-0 text-danger">La data di nascita è un campo obbligatorio</span>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <label for="birthNation" class="col-md-4 col-form-label text-md-end">Nazione di Nascita</label>
                            <div class="col-12 col-md-8">
                                <v-select :options="helpers.birth_nations" v-model="form.birth_nation" :clearable="false"></v-select>
                            </div>
                        </div>
                        <div class="row mb-3" v-if="form.birth_nation.id == 'IT'">
                            <label for="birthProvince" class="col-md-4 col-form-label text-md-end">Provincia di Nascita</label>
                            <div class="col-12 col-md-8">
                                <v-select :options="helpers.birth_provinces" v-model="form.birth_province" :clearable="false" @update:modelValue="changeBirthProvince">
                                    <template #search="{attributes, events}">
                                        <input class="vs__search" :required="!form.birth_province" v-bind="attributes" v-on="events" />
                                    </template>
                                </v-select>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <label for="birthCity" class="col-md-4 col-form-label text-md-end">Comune di Nascita</label>
                            <div class="col-12 col-md-8">
                                <v-select :options="helpers.birth_cities" v-model="form.birth_city" :clearable="false" v-if="form.birth_nation.id == 'IT'">
                                    <template #search="{attributes, events}">
                                        <input class="vs__search" :required="!form.birth_city" v-bind="attributes" v-on="events" />
                                    </template>
                                </v-select>
                                <input type="text" class="form-control" v-model="form.birth_city" id="birthCity" v-else required>
                            </div>
                        </div>
                    </div>
                    <div class="pt-3 d-flex justify-content-between">
                        <button class="btn btn-secondary" type="button" @click="goBackStep">
                            <i class="fas fa-angle-left me-2"></i>Indietro
                        </button>
                        <button class="btn btn-success" type="submit">
                            Prosegui<i class="fas fa-angle-right ms-2"></i>
                        </button>
                    </div>
                </form>
            </div>
        </transition>

        <!-- Step 3 -->
        <div v-if="step == 3">
            <h4 class="mb-3">Recapiti</h4>
            <form @submit.prevent="submitStep3">
                <div class="p-4 p-md-5 bg-white border rounded">
                    <div class="row mb-3">
                        <label for="email" class="col-md-4 col-form-label text-md-end">E-Mail</label>
                        <div class="col-12 col-md-8">
                            <input type="email" class="form-control" id="email" v-model="form.email" required>
                        </div>
                    </div>
                    <!-- Ripeti E-Mail -->
                    <div class="row mb-3">
                        <label for="email2" class="col-md-4 col-form-label text-md-end">Ripeti E-Mail</label>
                        <div class="col-12 col-md-8">
                            <input type="email" class="form-control" :class="{ 'is-invalid': !validation.email }" id="email2" v-model="form.email_2" required>
                            <div class="invalid-feedback" v-if="!validation.email">
                                La mail non corrisponde.
                            </div>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="telephone" class="col-md-4 col-form-label text-md-end">Recapito Telefonico</label>
                        <div class="col-12 col-md-8">
                            <input type="text" class="form-control" id="telephone" v-model="form.telephone" required :class="{ 'is-invalid': !isValidPhone && form.telephone != ''}">
                            <div class="invalid-feedback" v-if="!isValidPhone">
                                Formato recapito telefonico non valido
                            </div>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="addressNation" class="col-md-4 col-form-label text-md-end">Nazione di Residenza</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.address_nations" v-model="form.address_nation" :clearable="false"></v-select>
                        </div>
                    </div>
                    <div class="row mb-3" v-if="form.address_nation.id == 'IT'">
                        <label for="addressProvince" class="col-md-4 col-form-label text-md-end">Provincia di Residenza</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.address_provinces" v-model="form.address_province" :clearable="false" @update:modelValue="changeAddressProvince">
                                <template #search="{attributes, events}">
                                    <input class="vs__search" :required="!form.address_province" v-bind="attributes" v-on="events" />
                                </template>
                            </v-select>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="addressCity" class="col-md-4 col-form-label text-md-end">Comune di Residenza</label>
                        <div class="col-12 col-md-8">
                            <v-select :options="helpers.address_cities" v-model="form.address_city" :clearable="false" v-if="form.address_nation.id == 'IT'">
                            <template #search="{attributes, events}">
                                <input class="vs__search" :required="!form.address_city" v-bind="attributes" v-on="events" />
                            </template>
                        </v-select>
                            <input type="text" class="form-control" v-model="form.address_city" id="addressCity" v-else required>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="address" class="col-md-4 col-form-label text-md-end">Indirizzo di Residenza</label>
                        <div class="col-12 col-md-6">
                            <input type="text" class="form-control" v-model="form.address" id="address" required>
                        </div>
                        <div class="col-12 col-md-2">
                            <input type="text" class="form-control" v-model="form.address_number" placeholder="Civico" required>
                        </div>
                    </div>
                    <div class="row mb-3">
                        <label for="cap" class="col-md-4 col-form-label text-md-end">CAP</label>
                        <div class="col-12 col-md-3">
                            <input type="text" class="form-control" v-model="form.cap" id="cap" @input="checkCAP" required :class="{'is-invalid': !validation.cap}">
                            <div class="invalid-feedback" v-if="!validation.cap">Formato non valido</div>
                        </div>
                    </div>
                    <div class="alert alert-danger mt-3" role="alert" v-if="!validation.store">
                        <h5><i class="fas fa-exclamation-triangle me-2"></i>Impossibile proseguire</h5>
                        <p class="mb-0">Si è verificato un errore, contatta il supporto tecnico!</p>
                    </div>
                </div>
                <div class="pt-3 d-flex justify-content-between">
                    <button class="btn btn-secondary" @click="goBackStep">
                        <i class="fas fa-angle-left me-2"></i>Indietro
                    </button>
                    <button class="btn btn-success" type="submit" :disabled="helpers.sending || !validation.cap || !isValidPhone">
                        Prosegui
                        <i class="fas fa-circle-notch fa-spin ms-2" v-if="helpers.sending"></i>
                        <i class="fas fa-angle-right ms-2" v-else></i>
                    </button>
                </div>
            </form>
        </div>

        <!-- Step 4 -->
        <div v-if="step == 4">
            <h4 class="mb-3">Area personale</h4>
            <div class="p-4 p-md-5 bg-white border rounded">
                <p>Gentile <strong>{{ form.first_name }} {{ form.last_name }}</strong>,</p>
                <p>
                    la sua richiesta è andata a buon fine!<br />
                    Per poter completare la sua iscrizione le chiediamo di caricare i documenti richiesti 
                    nella sua pagina personale.
                </p>
                <a :href="access_link" target="_blank" class="btn btn-success">
                    <i class="fas fa-sign-in-alt me-2"></i>Accedi all'area personale
                </a>
            </div>
        </div>

    </div>
</template>

<script>
    import AppPlaceholder from '../ui/AppPlaceholder'
    import * as services from '../../services'

    export default {
        components: {
            AppPlaceholder,
        },
        props: {
            course: {
                type: Object
            },
            step: {
                type: Number,
                required: true
            }
        },
        data() {
            return {
                form: {
                    course: null,
                    course_select: null,
                    first_name: null,
                    last_name: null,
                    fiscal_code: null,
                    email: null,
                    email_2: null,
                    telephone: '',
                    privacy: 1,
                    marketing: 0,
                    statistics: 0,
                    ecp: null,
                    convention: null,
                    program: null,
                    installments: null,
                    gender: '',
                    birth_date: null,
                    birth_nation: { 'label': 'ITALIA', 'id': 'IT' },
                    birth_province: null,
                    birth_city: null,
                    address: null,
                    address_number: null,
                    address_nation: { 'label': 'ITALIA', 'id': 'IT' },
                    address_province: null,
                    address_city: null,
                    cap: null,
                    promo_code: null,
                    supplement_program_id: null,
                    mgm_code: null,
                    progetti:0,
                    alumni: 0
                },
                helpers: {
                    course: {},
                    courses: [],
                    ecps: [],
                    conventions: [],
                    programs: [],
                    birth_nations: [],
                    birth_provinces: [],
                    birth_cities: [],
                    address_nations: [],
                    address_provinces: [],
                    address_cities: [],
                    sending: false,
                    phoneRegex: /^(\+39|0039)?\s?3\d{2}\s?\d{6,7}$/
                },
                validation: {
                    email: true,
                    fiscal_code: true,
                    enrol: true,
                    message: null,
                    sendEmail: false,
                    store: true,
                    promo_code: null,
                    promo_code_message: null,
                    promo_code_type: null,
                    cap: true,
                    birth_date: false
                },
                temp: {
                    sendingEmail: false,
                    sendedEmail: false
                },
                access_link: null
            }
        },
        computed: {
            first_name() {
                return this.$store.getters['settings/first_name']
            },
            last_name() {
                return this.$store.getters['settings/last_name']
            },
            fiscal_code() {
                return this.$store.getters['settings/fiscal_code']
            },
            university() {
                return this.$store.getters['settings/from']
            },
            token() {
                return this.$store.getters['settings/token']
            },
            ecp() {
                return this.$store.getters['settings/ecp']
            },
            ecp_code() {
                return this.$store.getters['settings/ecp_code']
            },
            convention() {
                return this.$store.getters['settings/convention']
            },
            program() {
                return this.$store.getters['settings/program']
            },
            tracking() {
                return this.$store.getters['settings/tracking']
            },
            provider() {
                return this.$store.getters['settings/provider']
            },
            gads_conversion() {
                return this.$store.getters['settings/gads_conversion']
            },
            floodlight_conversion() {
                return this.$store.getters['settings/floodlight_conversion']
            },
            academic_year() {
                return this.$store.getters['settings/aa']
            },
            crm() {
                return this.$store.getters['settings/crm']
            },
            campaign() {
                return this.$store.getters['settings/campaign']
            },
            source() {
                return this.$store.getters['settings/source']
            },
            isValidPhone() {
                if (this.form.telephone == '') return false;
                return this.helpers.phoneRegex.test(this.form.telephone);
            }
        },
        methods: {
            async getCourses() {
                const data = await services.ecp.get('api/get/oc/courses');
                let courses
                if (this.university.crm == 'SOLE24ORE') {
                    courses = data.data.filter((el) => el.code.slice(0, 2) == 'MA')
                } else {
                    courses = data.data
                }
                this.helpers.courses = courses.map(el => {
                    return {
                        label: el.code + ' - ' + el.title,
                        id: el.code
                    }
                })
            },
            async changeCourse() {
                const select_id = this.form.course_select.id
                const data = await services.ecp.get(`api/get/oc/course/${select_id}`)
                this.form.course = data.data
                this.$store.dispatch('summary/setCourse', this.form.course.title)
                this.$store.dispatch('summary/setCategory', this.form.course.description)
                this.form.program = null
                this.form.installments = null
                await this.getConventions()
                await this.getPrograms()
            },
            async getEcp() {
                const data = await services.ecp.get('api/get/ecp-active-no-master')
                this.helpers.ecps = data.data.map(el => {
                    return {
                        'label': el.multiorienta_code.toUpperCase(),
                        'id': el.user_id,
                        'codice': el.codice.toUpperCase(),
                        'multiorienta_id': el.multiorienta_id 
                    }
                })
                if (!this.ecp && !this.ecp_code) {
                    this.form.ecp = this.helpers.ecps.find(el => el.label == 'DIREZIONALE')
                } else if (this.ecp_code) {
                    let find_ecp = this.helpers.ecps.find(el => el.label == this.ecp_code)
                    if (typeof find_ecp !== 'undefined') {
                        this.form.ecp = find_ecp
                    } else {
                        this.form.ecp = this.helpers.ecps.find(el => el.label == 'DIREZIONALE')
                    }
                } else {
                    this.form.ecp = this.helpers.ecps.find(el => el.id == this.ecp)
                }
            },
            async getPromoCode() {
                if (!this.form.fiscal_code) {
                    this.validation.promo_code = 'fail'
                    this.validation.promo_code_message = 'E\' necessario il codice fiscale'
                    return
                }
                if (!this.form.promo_code) {
                    this.validation.promo_code = null
                    this.validation.promo_code_message = null
                    return
                }
                this.form.supplement_program_id = null
                const {data} = await services.ecp.post('api/check-promo-code', {
                    promo_code: this.form.promo_code,
                    fiscal_code: this.form.fiscal_code,
                    course_code: this.form.course.code,
                    convention_id: this.form.convention.id,
                    program_id: this.form.program.id,
                })
                const el = data
                this.validation.promo_code = el.status
                if (el.status == 'fail') {
                    this.validation.promo_code_message = 'Codice promo non valido'
                }
                this.validation.promo_code_type = el.type
                if (el.type == 'SupplementProgram') {
                    this.form.supplement_program_id = el.data.id
                    this.getImport()
                }
                if (el.type == 'program') {
                    this.form.program = {
                        id: el.data.id,
                        label: el.data.name 
                    }
                    this.form.prospect = null
                    this.getImport()
                }
            },
            async getConventions() {
                const data = await services.ecp.post('api/get/graduate/conventions', {
                    ecp_id: this.form.ecp.id
                })
                this.helpers.conventions = data.data.map(el => {
                    return {
                        'label': el.name.toUpperCase(),
                        'id': el.id
                    }
                })
                if (!this.convention) {
                    this.form.convention = this.helpers.conventions.find(el => el.label == 'STANDARD' || el.label == 'STANDARD-UTP' || el.label == 'STANDARD-UTSR')
                } else {
                    this.form.convention = this.helpers.conventions.find(el => el.id == this.convention)
                }
            },
            async getPrograms() {
                if (this.validation.promo_code_type == 'program') return
                const course_id = this.form.course.id
                const data = await services.ecp.post('api/get/oc/' + course_id + '/programs')
                this.helpers.programs = data.data.map(el => {
                    return {
                        'label': el.class + ' (' + el.mp_tot+ '€)',
                        'id': el.id_payment
                    }
                })
                if (this.program) {
                    this.form.program = this.helpers.programs.find(el => el.id == this.program)
                    this.getImport()
                }
            },
            async getImport() {
                const program_id = this.form.program.id 
                const data = await services.ecp.post('api/get/oc/prospectus', {
                    program: program_id,
                    promo_code: this.form.promo_code,
                    fiscal_code: this.form.fiscal_code,
                    convention_id: this.form.convention.id
                })
                this.form.installments = data.data
            },
            async getNations() {
                const data = await services.ecp.post('api/get/nations')
                return data.data.map(el => {
                    return {
                        'label': el.NAZIONE,
                        'id': el.ID_NAZIONALITA
                    }
                })
            },
            async getProvinces() {
                const data = await services.ecp.post('api/get/province')
                return data.data.map(el => {
                    return {
                        'label': el.PROVINCIA,
                        'id': el.SIGLA
                    }
                })
            },
            async changeBirthProvince() {
                const province_id = this.form.birth_province.id
                this.form.birth_city = null
                this.helpers.birth_cities = await this.getCities(province_id)
            },
            async changeAddressProvince() {
                const province_id = this.form.address_province.id
                this.form.address_city = null
                this.helpers.address_cities = await this.getCities(province_id)
            },
            async getCities(province_id) {
                const data = await services.ecp.post('api/get/cities', {
                    province: province_id
                })
                return data.data.map(el => {
                    return {
                        'label': el.COMUNE,
                        'id': el.COD_ISTAT
                    }
                })
            },
            checkCAP() {
                if (this.form.cap == '') {
                    this.validation.cap = true
                    return
                }
                let pattern = new RegExp('^[0-9]{5,5}$');
                if (!this.form.cap.match(pattern)) {
                    this.validation.cap = false
                } else {
                    this.validation.cap = true
                }
            },
            goBackStep() {
                const step = this.step - 1
                this.$emit('update', step)
            },
            async submitStep1() {
                this.helpers.sending = true
                /*try {
                    const check_fc = await axios.post('https://aws-gammaapi.multiversity.click/check-name-fiscal-code/eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOjE4NTE2LCJpc3MiOiJQRUdBU08i', {
                        fiscalCode: this.form.fiscal_code,
                        name: this.form.first_name,
                        surname: this.form.last_name
                    }, {
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    this.validation.fiscal_code = check_fc.data.data
                } catch (error) {
                    this.validation.fiscal_code = false
                }*/
                try {
                    const check_enrol = await services.ecp.post('api/get/oc/fiscal/code/check', {
                        courseCode: this.form.course.code,
                        fiscalCode: this.form.fiscal_code,
                    })
                    this.validation.enrol = true
                } catch (error) {
                    this.validation.enrol = false
                    const response = error.response
                    switch (response.status) {
                        case 409:
                            this.validation.message = response.data
                            this.validation.sendEmail = true 
                            break 
                        case 403:
                            this.validation.message = response.data
                            break 
                        default:
                            this.validation.message = response.data
                            break
                    }
                }
                this.helpers.sending = false
                this.validation.email = this.form.email == this.form.email_2
                if (this.validation.enrol && this.validation.email) {
                    const step = this.step + 1
                    this.$emit('update', step)
                }
            },
            async sendEmailTemp() {
                this.temp.sendingEmail = true
                try {
                    const data = await services.ecp.post('api/send/oc/enrolment/online/mail', {
                        courseCode: this.form.course.code,
                        fiscalCode: this.form.fiscal_code
                    })
                    this.temp.sendingEmail = false
                    this.temp.sendedEmail = true
                } catch(error) {
                    this.temp.sendingEmail = false
                    console.log(error)
                }
            },
            submitStep2() {
                this.validation.birth_date = this.form.birth_date == '' || this.form.birth_date === null ? true : false
                if (this.validation.birth_date) return
                const step = this.step + 1
                this.$emit('update', step)
            },
            async submitStep3() {
                this.helpers.sending = true
                const form = this.form
                const birth_date = form.birth_date.split('-')
                let request = {}
                let crm_id = 0
                this.helpers.sending = true
                let sendToEnrol = {}
                let storeInCrm = true

                // crm
                if (this.crm === true) {
                    try {
                        const crmFields = {
                            from: this.university.crm,
                            first_name: form.first_name,
                            last_name: form.last_name,
                            email: form.email,
                            telephone: form.telephone,
                            fiscal_code: form.fiscal_code,
                            birth_date: form.birth_date,
                            gender: form.gender,
                            province: form.address_nation.id == 'IT' ? form.address_province.label : form.address_province,
                            city: form.address_nation.id == 'IT' ? form.address_city.label : form.address_city,
                            type: 4,
                            category: this.$crm_oc_category(this.form.course),
                            subject: this.form.course.title,
                            provider: this.provider,
                            campaign: this.campaign ? this.campaign : this.$crm.get('campaign'),
                            privacy: 1,
                            marketing: form.marketing,
                            statistics: form.statistics,
                            ga_id: this.$crm.get('_ga'),
                            gclid: this.$crm.get('gclid'),
                            hubspotutk: this.$crm.get('hubspotutk'),
                            device: this.$crm.getDeviceInfo(),
                            pages: this.$crm.getPages(),
                            time: this.$crm.getTime(),
                            code: form.course.code,
                            enrol_type: 'Postlaurea',
                            classform: 0,
                            ecp: form.ecp.codice,
                            multiorienta_id: form.ecp.multiorienta_id,
                            convention: form.convention.label, 
                            program: form.program.label, 
                            import: form.installments.program.mp_tot
                        }
                        const sendToCrm = await services.crm.post('crm/public/request', crmFields)
                        request = sendToCrm.data.data.request
                        crm_id = sendToCrm.data.data.request.id
                        storeInCrm = true
                    } catch (error) {
                        storeInCrm = false
                        console.log(error)
                    }
                }

                // segreteria
                if (storeInCrm) {
                    try {
                        const enrolFields = {
                            courseCode: form.course.code,
                            ecp: form.ecp.id,
                            convention: form.convention.id,
                            program: form.program.id,
                            firstname: form.first_name,
                            lastname: form.last_name,
                            fiscalCode: form.fiscal_code,
                            email: form.email,
                            email_confirmation: form.email_2,
                            phone: form.telephone,
                            gender: form.gender,
                            birthdate: birth_date[2] + '/' + birth_date[1] + '/' + birth_date[0],
                            birthnation: form.birth_nation.id,
                            birthprovince: form.birth_nation.id == 'IT' ? form.birth_province.id : null,
                            birthcity: form.birth_nation.id == 'IT' ? form.birth_city.id : form.birth_city,
                            address: form.address,
                            address_n: form.address_number,
                            nation: form.address_nation.id,
                            province: form.address_nation.id == 'IT' ? form.address_province.id : null,
                            city: form.address_nation.id == 'IT' ? form.address_city.id : form.address_city,
                            cap: form.cap,
                            crm_id: crm_id,
                            supplement_program_id: form.supplement_program_id,
                            promo_code: form.promo_code,
                            mgm_code: form.mgm_code,
                            multiorienta_id: form.ecp.multiorienta_id,
                            source: this.source
                        }
                        sendToEnrol = await services.ecp.post('api/send/oc/enrolment/online/web', enrolFields)
                        this.validation.store = true
                        this.access_link = 'https://' + this.university.api + '.multiversity.click/main/online/oc_registration.php?online_token=' + sendToEnrol.data.token 
                    } catch (error) {
                        this.validation.store = false
                        console.log(error)
                    }
                } else {
                    this.validation.store = false
                }

                // tracking
                if (this.tracking === true && storeInCrm && this.validation.store) {
                   if (this.university.crm == 'MERCATORUM' || this.university.crm == 'SANRAFFAELE') {
                        this.$gtm.trackOnlineRegistration(
                            'OC', //registration_type
                            this.university.crm, //from
                            form.course.title, //course_name
                            form.course.code, //transaction_id - course_code chiedere
                            crm_id, //contact_id
                            Number(this.$store.getters['summary/mp_tot'] || 0), //value importo
                            form.gender, //user_gender
                            birth_date[2] + '/' + birth_date[1] + '/' + birth_date[0], //user_birth_date
                            form.ecp.codice, //ecp_code
                            form.convention.label,//entrance_type
                            form.email, //user_data.email
                            form.telephone, //user_data.phone_number
                            form.first_name, //user_data.first_name
                            form.last_name,//user_data.last_name
                            form.address_city.label, //user_data.address.city
                            ); 
                    } else {
                        if (this.$CookieConsent.acceptedCategory('analytics')) {
                            this.$crm.trackGAEnrol('OC', form.installments.program.mp_tot)
                        }
                        if (this.$CookieConsent.acceptedCategory('marketing')) {
                            if (this.gads_conversion) {
                                this.$crm.trackGoogleAdsEnrol(this.gads_conversion, 'OC', form.installments.program.mp_tot, form.course.title.substring(0, 100))
                            }
                            if (this.floodlight_conversion) {
                                this.$crm.trackFloodlightEnrol(
                                    this.floodlight_conversion,
                                    crm_id, 
                                    'OC', 
                                    this.form.course.title.substring(0, 100), 
                                    form.installments.program.mp_tot,
                                    form.gender,
                                    form.birth_date,
                                    form.ecp.label,
                                    this.academic_year,
                                    this.$crm.get('_ga'),
                                    request.contact_id
                                )
                            }
                            this.$crm.trackFacebookEnrol(crm_id)
                            this.$crm.trackBingAdsEnrol('OC', form.installments.program.mp_tot)
                            this.$crm.trackEnhancedConversion(request.contact.email, request.contact.telephone)
                            this.$crm.trackCdpLead(request.contact_id, request.subject, 4)
                        }
                        this.$crm.resetTracking()
                    }
                }

                this.helpers.sending = false
                if (this.validation.store) {
                    const step = this.step + 1
                    this.$emit('update', step)
                }
            }
        },
        async mounted() {
            await this.getEcp()
            await this.getConventions()
            if (!this.course) {
                await this.getCourses()
            } else {
                this.form.course = this.course
                this.$store.dispatch('summary/setCourse', this.form.course.title)
                await this.getPrograms()
            }
            const nations = await this.getNations()
            const provinces = await this.getProvinces()
            this.helpers.birth_nations = nations
            this.helpers.address_nations = nations
            this.helpers.birth_provinces = provinces
            this.helpers.address_provinces = provinces
            if (this.first_name) this.form.first_name = this.first_name
            if (this.last_name) this.form.last_name = this.last_name
            if (this.fiscal_code) this.form.fiscal_code = this.fiscal_code
        },
        watch: {
            'form.ecp' (val) {
                this.$store.dispatch('summary/setEcp', val.label)
            },
            'form.convention' (val) {
                this.$store.dispatch('summary/setConvention', val.label)
            },
            'form.program' (val) {
                if (val) {
                    this.$store.dispatch('summary/setProgram', val.label)
                } else {
                    this.$store.dispatch('summary/setProgram', null)
                }
            },
            'form.installments' (val) {
                if (val) {
                    this.$store.dispatch('summary/setRate', val.installments.length)
                    this.$store.dispatch('summary/setImport', val.program.mp_tot)
                    this.$store.dispatch('summary/setInstallments', val.installments)
                } else {
                    this.$store.dispatch('summary/setRate', null)
                    this.$store.dispatch('summary/setImport', null)
                    this.$store.dispatch('summary/setInstallments', null)
                }
            },
            'form.birth_date' (val) {
                console.log(val)
            }
        }
    }
</script>